import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { 
  Plus, 
  Search, 
  Filter, 
  Download, 
  Upload,
  Edit,
  Trash2,
  Mail,
  User,
  Building,
  X,
  Save,
  UserPlus,
  Send,
  Eye,
  FileText
} from 'lucide-react'
import toast from 'react-hot-toast'

import { useAuth } from '../../hooks/useAuth'
import { db } from '../../lib/supabase'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { Card } from '../../components/ui/Card'
import { Navigation } from '../../components/Navigation'
import { sendEmail, generateAssessmentInvitationEmail } from '../../lib/email'
import type { Employee } from '../../types/database'

const employeeSchema = z.object({
  firstName: z.string().min(2, 'First name must be at least 2 characters'),
  lastName: z.string().min(2, 'Last name must be at least 2 characters'),
  email: z.string().email('Please enter a valid email address'),
  department: z.string().optional(),
  companyEmployeeId: z.string().optional(),
})

type EmployeeFormData = z.infer<typeof employeeSchema>

export const EmployeeManagement: React.FC = () => {
  const { appUser } = useAuth()
  const [employees, setEmployees] = useState<Employee[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [showAddModal, setShowAddModal] = useState(false)
  const [editingEmployee, setEditingEmployee] = useState<Employee | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [sendingInvite, setSendingInvite] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue
  } = useForm<EmployeeFormData>({
    resolver: zodResolver(employeeSchema),
  })

  useEffect(() => {
    if (appUser?.company_id) {
      loadEmployees()
    } else {
      console.error('No company ID found for user:', appUser)
      setLoading(false)
    }
  }, [appUser])

  const loadEmployees = async () => {
    if (!appUser?.company_id) {
      toast.error('No company ID found. Please ensure you have a company set up.')
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      const { data, error } = await db.employees.getByCompany(appUser.company_id)
      if (error) throw error
      setEmployees(data || [])
    } catch (error) {
      console.error('Error loading employees:', error)
      toast.error('Failed to load employees')
    } finally {
      setLoading(false)
    }
  }

  const filteredEmployees = employees.filter(employee =>
    employee.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    employee.last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    employee.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    employee.department?.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const handleAddEmployee = () => {
    setEditingEmployee(null)
    reset()
    setShowAddModal(true)
  }

  const handleEditEmployee = (employee: Employee) => {
    setEditingEmployee(employee)
    setValue('firstName', employee.first_name)
    setValue('lastName', employee.last_name)
    setValue('email', employee.email)
    setValue('department', employee.department || '')
    setValue('companyEmployeeId', employee.company_employee_id || '')
    setShowAddModal(true)
  }

  const handleDeleteEmployee = async (employee: Employee) => {
    if (!confirm(`Are you sure you want to delete ${employee.first_name} ${employee.last_name}?`)) {
      return
    }

    try {
      const { error } = await db.employees.delete(employee.id)
      if (error) throw error
      
      toast.success('Employee deleted successfully')
      loadEmployees()
    } catch (error) {
      console.error('Error deleting employee:', error)
      toast.error('Failed to delete employee')
    }
  }

  const onSubmit = async (data: EmployeeFormData) => {
    if (!appUser?.company_id) {
      toast.error('No company ID found. Please ensure you have a company set up.')
      return
    }

    setSubmitting(true)
    try {
      if (editingEmployee) {
        // Update existing employee
        const { error } = await db.employees.update(editingEmployee.id, {
          first_name: data.firstName,
          last_name: data.lastName,
          email: data.email,
          department: data.department || null,
          company_employee_id: data.companyEmployeeId || null,
        })
        
        if (error) throw error
        toast.success('Employee updated successfully')
      } else {
        // Create new employee
        const { error } = await db.employees.create({
          company_id: appUser.company_id,
          first_name: data.firstName,
          last_name: data.lastName,
          email: data.email,
          department: data.department || null,
          company_employee_id: data.companyEmployeeId || null,
        })
        
        if (error) throw error
        toast.success('Employee added successfully')
      }
      
      setShowAddModal(false)
      reset()
      loadEmployees()
    } catch (error: any) {
      console.error('Error saving employee:', error)
      if (error.code === '23505') {
        toast.error('Employee email already exists in this company')
      } else {
        toast.error('Failed to save employee')
      }
    } finally {
      setSubmitting(false)
    }
  }

  const handleSendAssessmentInvite = async (employee: Employee) => {
    setSendingInvite(employee.id)
    try {
      // Generate unique assessment token
      const token = generateAssessmentToken()
      const expiresAt = new Date()
      expiresAt.setDate(expiresAt.getDate() + 7) // Token expires in 7 days

      // Save token to database
      const { error: tokenError } = await db.assessmentTokens.create({
        employee_id: employee.id,
        token,
        expires_at: expiresAt.toISOString()
      })

      if (tokenError) throw tokenError

      // Create assessment link
      const assessmentLink = `${window.location.origin}/assessment/${token}`

      // Get company name
      const companyName = appUser?.company?.name || 'Your Company'

      // Generate email HTML
      const emailHtml = generateAssessmentInvitationEmail(
        `${employee.first_name} ${employee.last_name}`,
        companyName,
        assessmentLink
      )

      // Send email
      const result = await sendEmail({
        to: employee.email,
        subject: 'Your Wellness Assessment Invitation',
        html: emailHtml
      })

      if (!result.success) {
        throw new Error(result.error || 'Failed to send email')
      }
      
      toast.success(`Assessment invitation sent to ${employee.first_name} ${employee.last_name}`)
    } catch (error) {
      console.error('Error sending assessment invite:', error)
      toast.error('Failed to send assessment invitation')
    } finally {
      setSendingInvite(null)
    }
  }

  const generateAssessmentToken = (): string => {
    return 'assess_' + Math.random().toString(36).substring(2) + Date.now().toString(36)
  }

  const handleImportCSV = () => {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = '.csv'
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0]
      if (!file) return

      if (!appUser?.company_id) {
        toast.error('No company ID found')
        return
      }

      try {
        const text = await file.text()
        const lines = text.split('\n')
        const headers = lines[0].split(',').map(h => h.trim().toLowerCase())
        
        const requiredHeaders = ['first_name', 'last_name', 'email']
        const missingHeaders = requiredHeaders.filter(h => !headers.includes(h))
        
        if (missingHeaders.length > 0) {
          toast.error(`Missing required columns: ${missingHeaders.join(', ')}`)
          return
        }

        const employeesToImport = []
        for (let i = 1; i < lines.length; i++) {
          if (!lines[i].trim()) continue // Skip empty lines
          
          const values = lines[i].split(',').map(v => v.trim())
          if (values.length < 3) continue

          const employee = {
            company_id: appUser.company_id,
            first_name: values[headers.indexOf('first_name')],
            last_name: values[headers.indexOf('last_name')],
            email: values[headers.indexOf('email')],
            department: headers.includes('department') ? values[headers.indexOf('department')] : null,
            company_employee_id: headers.includes('employee_id') ? values[headers.indexOf('employee_id')] : null,
          }

          employeesToImport.push(employee)
        }

        // Import employees
        let successCount = 0
        let errorCount = 0

        for (const emp of employeesToImport) {
          try {
            // Check if employee with this email already exists in this company
            const { data: existingEmployees } = await db.employees.getByCompanyAndEmail(
              appUser.company_id, 
              emp.email
            )
            
            if (existingEmployees && existingEmployees.length > 0) {
              console.log(`Employee with email ${emp.email} already exists, skipping`)
              errorCount++
              continue
            }
            
            const { error } = await db.employees.create(emp)
            if (error) {
              if (error.code === '23505') {
                console.log(`Duplicate employee: ${emp.email}`)
              } else {
                throw error
              }
              errorCount++
            } else {
              successCount++
            }
          } catch (error) {
            console.error('Error importing employee:', emp.email, error)
            errorCount++
          }
        }

        toast.success(`Imported ${successCount} employees successfully. ${errorCount} errors.`)
        loadEmployees()
      } catch (error) {
        console.error('Error importing CSV:', error)
        toast.error('Failed to import CSV file')
      }
    }
    input.click()
  }

  const handleExport = () => {
    if (employees.length === 0) {
      toast.error('No employees to export')
      return
    }

    // Create CSV content
    const headers = ['First Name', 'Last Name', 'Email', 'Department', 'Employee ID', 'Created Date']
    const csvContent = [
      headers.join(','),
      ...employees.map(emp => [
        emp.first_name,
        emp.last_name,
        emp.email,
        emp.department || '',
        emp.company_employee_id || '',
        new Date(emp.created_at).toLocaleDateString()
      ].join(','))
    ].join('\n')

    // Download CSV
    const blob = new Blob([csvContent], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `employees-${new Date().toISOString().split('T')[0]}.csv`
    a.click()
    window.URL.revokeObjectURL(url)
    
    toast.success('Employee list exported successfully')
  }

  const getDepartmentStats = () => {
    const departments = employees.reduce((acc, emp) => {
      const dept = emp.department || 'Unassigned'
      acc[dept] = (acc[dept] || 0) + 1
      return acc
    }, {} as Record<string, number>)

    return Object.entries(departments).map(([name, count]) => ({ name, count }))
  }

  if (!appUser?.company_id) {
    return (
      <div className="min-h-screen bg-calming">
        <Navigation variant="dashboard" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Card className="p-8 text-center">
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Building className="w-8 h-8 text-amber-600" />
            </div>
            <h2 className="text-xl font-semibold text-slate-900 mb-2">
              Company Setup Required
            </h2>
            <p className="text-slate-600 mb-6">
              You need to set up your company before managing employees.
            </p>
            <Button
              onClick={() => window.location.href = '/dashboard'}
              className="bg-sage-600 hover:bg-sage-700 text-white"
            >
              Go to Dashboard
            </Button>
          </Card>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-calming">
      <Navigation variant="dashboard" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 mb-2">
              Team Management
            </h1>
            <p className="text-slate-600">
              Manage your team members and send wellness assessment invitations
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 mt-4 sm:mt-0">
            <Button variant="outline" onClick={handleImportCSV} className="border-slate-300 text-slate-700 hover:bg-slate-50">
              <Upload className="w-4 h-4 mr-2" />
              Import CSV
            </Button>
            <Button variant="outline" onClick={handleExport} className="border-slate-300 text-slate-700 hover:bg-slate-50">
              <Download className="w-4 h-4 mr-2" />
              Export
            </Button>
            <Button onClick={handleAddEmployee} className="bg-sage-600 hover:bg-sage-700 text-white">
              <Plus className="w-4 h-4 mr-2" />
              Add Team Member
            </Button>
          </div>
        </div>

        {/* Search and Filters */}
        <Card className="mb-8">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                <Input
                  placeholder="Search team members..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 border-slate-200 focus:border-sage-500 focus:ring-sage-500/20"
                />
              </div>
            </div>
            <Button variant="outline" className="border-slate-300 text-slate-700 hover:bg-slate-50">
              <Filter className="w-4 h-4 mr-2" />
              Filters
            </Button>
          </div>
        </Card>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card>
            <div className="flex items-center">
              <div className="p-2 bg-sage-100 rounded-lg">
                <User className="w-6 h-6 text-sage-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-slate-600">Total Members</p>
                <p className="text-2xl font-bold text-slate-900">{employees.length}</p>
              </div>
            </div>
          </Card>
          <Card>
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Building className="w-6 h-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-slate-600">Departments</p>
                <p className="text-2xl font-bold text-slate-900">
                  {getDepartmentStats().length}
                </p>
              </div>
            </div>
          </Card>
          <Card>
            <div className="flex items-center">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <Send className="w-6 h-6 text-emerald-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-slate-600">Invitations Sent</p>
                <p className="text-2xl font-bold text-slate-900">0</p>
                <p className="text-xs text-slate-500">This session</p>
              </div>
            </div>
          </Card>
          <Card>
            <div className="flex items-center">
              <div className="p-2 bg-amber-100 rounded-lg">
                <FileText className="w-6 h-6 text-amber-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-slate-600">Assessments</p>
                <p className="text-2xl font-bold text-slate-900">0</p>
                <p className="text-xs text-slate-500">Completed</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Employee List */}
        <Card>
          <div className="overflow-x-auto">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sage-500 mx-auto mb-4"></div>
                  <p className="text-slate-600">Loading team members...</p>
                </div>
              </div>
            ) : filteredEmployees.length === 0 ? (
              <div className="text-center py-12">
                <User className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-900 mb-2">
                  {searchTerm ? 'No team members found' : 'No team members yet'}
                </h3>
                <p className="text-slate-600 mb-6">
                  {searchTerm 
                    ? 'Try adjusting your search terms'
                    : 'Get started by adding your first team member'
                  }
                </p>
                {!searchTerm && (
                  <Button onClick={handleAddEmployee} className="bg-sage-600 hover:bg-sage-700 text-white">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Team Member
                  </Button>
                )}
              </div>
            ) : (
              <table className="min-w-full divide-y divide-slate-200">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Team Member
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Department
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Employee ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-slate-200">
                  {filteredEmployees.map((employee) => (
                    <motion.tr
                      key={employee.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="hover:bg-slate-50"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-10 h-10 bg-sage-100 rounded-full flex items-center justify-center">
                            <span className="text-sage-600 font-medium">
                              {employee.first_name[0]}{employee.last_name[0]}
                            </span>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-slate-900">
                              {employee.first_name} {employee.last_name}
                            </div>
                            <div className="text-sm text-slate-500">
                              {employee.user_id ? 'Account linked' : 'No account'}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                        {employee.email}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                        {employee.department || 'Not assigned'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                        {employee.company_employee_id || 'Not set'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleSendAssessmentInvite(employee)}
                            disabled={sendingInvite === employee.id}
                            className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                          >
                            {sendingInvite === employee.id ? (
                              <div className="w-4 h-4 animate-spin rounded-full border-2 border-emerald-600 border-t-transparent" />
                            ) : (
                              <Send className="w-4 h-4" />
                            )}
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleEditEmployee(employee)}
                            className="text-slate-600 hover:text-slate-900 hover:bg-slate-100"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleDeleteEmployee(employee)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </Card>
      </div>

      {/* Add/Edit Employee Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-slate-900">
                  {editingEmployee ? 'Edit Team Member' : 'Add Team Member'}
                </h3>
                <Button
                  variant="ghost"
                  onClick={() => setShowAddModal(false)}
                  className="text-slate-400 hover:text-slate-600"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <Input
                    label="First Name *"
                    {...register('firstName')}
                    error={errors.firstName?.message}
                    placeholder="John"
                    className="border-slate-200 focus:border-sage-500 focus:ring-sage-500/20"
                  />
                  <Input
                    label="Last Name *"
                    {...register('lastName')}
                    error={errors.lastName?.message}
                    placeholder="Doe"
                    className="border-slate-200 focus:border-sage-500 focus:ring-sage-500/20"
                  />
                </div>

                <Input
                  label="Email *"
                  type="email"
                  {...register('email')}
                  error={errors.email?.message}
                  placeholder="john.doe@company.com"
                  className="border-slate-200 focus:border-sage-500 focus:ring-sage-500/20"
                />

                <Input
                  label="Department"
                  {...register('department')}
                  error={errors.department?.message}
                  placeholder="Engineering, Marketing, Sales..."
                  className="border-slate-200 focus:border-sage-500 focus:ring-sage-500/20"
                />

                <Input
                  label="Employee ID"
                  {...register('companyEmployeeId')}
                  error={errors.companyEmployeeId?.message}
                  placeholder="EMP001"
                  className="border-slate-200 focus:border-sage-500 focus:ring-sage-500/20"
                />

                <div className="flex gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowAddModal(false)}
                    className="flex-1 border-slate-300 text-slate-700 hover:bg-slate-50"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    loading={submitting}
                    className="flex-1 bg-sage-600 hover:bg-sage-700 text-white"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {editingEmployee ? 'Update' : 'Add'} Member
                  </Button>
                </div>
              </form>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  )
}